import { useEffect, useState } from 'react';
import Typography from '@mui/material/Typography';
import ConfirmDialog from 'further-ui/components/ConfirmDialog';
import { Column } from 'further-ui/components/Layout';
import {
  CalendarDay,
  downloadFiles,
  downloadLinkAsFile,
} from 'further-ui/utils';
import MultipleAutocomplete from 'components/MultipleAutocomplete';

export type PdfDownloadRow = {
  id: string;
  name: string;
  sortDate: CalendarDay;
  previewUrl: string;
  downloadFileName: string;
};

type Props = {
  open: boolean;
  onClose: () => void;
  onDownloadError: (message: string) => void;
  rows: Array<PdfDownloadRow>;
  zipName: string;
};

const SelectPdfDownloadsModal: React.FC<Props> = ({
  open,
  onClose,
  onDownloadError,
  rows,
  zipName,
}) => {
  const [isDownloading, setIsDownloading] = useState(false);
  const [selectedIds, setSelectedIds] = useState<Array<string>>([]);

  useEffect(() => {
    if (open) {
      setSelectedIds([]);
    }
  }, [open, rows]);

  const handleDownload = async () => {
    const selectedRows = rows.filter((row) => selectedIds.includes(row.id));
    if (selectedRows.length === 0) {
      return;
    }

    try {
      setIsDownloading(true);

      if (selectedRows.length === 1) {
        const row = selectedRows[0];
        await downloadLinkAsFile(
          row.previewUrl,
          row.downloadFileName,
          (message) => onDownloadError(message),
        );
        onClose();
        return;
      }

      await downloadFiles(
        selectedRows.map((row) => ({
          url: row.previewUrl,
          name: row.downloadFileName,
        })),
        `${zipName}.zip`,
      );
      onClose();
    } catch {
      onDownloadError('Failed to download selected files.');
    } finally {
      setIsDownloading(false);
    }
  };

  return (
    <ConfirmDialog
      open={open}
      onClose={onClose}
      loading={isDownloading}
      title="Select files to download"
      maxWidth="sm"
      content={
        <Column spacing="md" fullWidth>
          <Typography variant="body2">
            Please select the files you wish to download.
          </Typography>
          <MultipleAutocomplete
            name="pdf-download-selection"
            placeholder="Select files"
            options={rows.map((row) => ({
              value: row.id,
              label: `${row.name} — ${row.sortDate.toReadable()}`,
            }))}
            value={selectedIds}
            onChange={setSelectedIds}
          />
        </Column>
      }
      actions={{
        cancel: {
          label: 'Cancel',
          onClick: onClose,
        },
        confirm: {
          label: 'Download',
          onClick: handleDownload,
          disabled: selectedIds.length === 0,
        },
      }}
    />
  );
};

export default SelectPdfDownloadsModal;
